import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, HostListener, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgentRunService, AuthService, TelemetryService } from '#services';
import { TelemetryEvent } from '#enums';
import { AgentRun, parseUtcTimestamp, toWatDateIso, WAT_TIME_ZONE } from '#models';
import { environment } from '#environments';
import { AgentRunsDateRangeComponent, DateRange } from './date-range/agent-runs-date-range.component';
import { AgentRunDrawerComponent } from './drawer/agent-run-drawer.component';
import { DrawerVm, HopVm, RerunFeedback, StatusMeta, WaterfallSeg } from './agent-runs.models';

interface Segments {
  bus: number;
  pickup: number;
  run: number;
  total: number;
}

interface MiniBar {
  bus: number;
  pickup: number;
  run: number;
}

interface RunRowVm {
  run: AgentRun;
  status: StatusMeta;
  isRerun: boolean;
  runForDate: string;
  completedText: string | null;
  completedInProgress: boolean;
  summary: string | null;
  miniBar: MiniBar | null;
  miniBarFailed: boolean;
  miniBarOpen: boolean;
  durationText: string;
  durationOpen: boolean;
}

interface Kpis {
  total: number;
  succeeded: number;
  failed: number;
  inflight: number;
  reruns: number;
  avg: string;
  succRate: number;
}

interface GroupVm {
  groupKey: string;
  groupLabel: string;
  attemptCount: number;
  hasReruns: boolean;
  isCollapsed: boolean;
  rows: RunRowVm[];
}

const STATUS_META: Record<string, StatusMeta> = {
  Pending: { cls: 'st-pending', label: 'Pending', live: false },
  Running: { cls: 'st-running', label: 'Running', live: true },
  Succeeded: { cls: 'st-succeeded', label: 'Succeeded', live: false },
  Failed: { cls: 'st-failed', label: 'Failed', live: false },
  Cancelled: { cls: 'st-cancelled', label: 'Cancelled', live: false },
  TimedOut: { cls: 'st-timedout', label: 'Timed Out', live: false },
};
const ACTIVE_STATUSES = new Set(['Pending', 'Running']);
const STOPPING_META: StatusMeta = { cls: 'st-cancelled', label: 'Stopping…', live: true };
// Runs take minutes, so a slow refresh is enough to show a stop finishing.
const STOP_REFRESH_MS = 15_000;

function statusMeta(status: string): StatusMeta {
  return STATUS_META[status] ?? { cls: 'st-pending', label: status || '—', live: false };
}
function isStopping(r: AgentRun): boolean {
  return !!r.cancelRequestedAt && ACTIVE_STATUSES.has(r.runStatus);
}
function runStatusMeta(r: AgentRun): StatusMeta {
  return isStopping(r) ? STOPPING_META : statusMeta(r.runStatus);
}
function parseDate(t: string | null): Date | null {
  return parseUtcTimestamp(t);
}
function fmtTime(t: string | null): string | null {
  const date = parseDate(t);
  if (!date) {
    return null;
  }
  const formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: WAT_TIME_ZONE,
  });
  return `${formatted} WAT`;
}
function fmtDur(ms: number | null): string {
  if (ms == null) {
    return '—';
  }
  const clamped = ms < 0 ? 0 : ms;
  const s = Math.round(clamped / 1000);
  if (s < 60) {
    return `${s}s`;
  }
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) {
    return r ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}
function runDurationMs(r: AgentRun, now: number): number | null {
  const start = parseDate(r.agentRanTaskTime);
  if (!start) {
    return null;
  }
  const end = parseDate(r.agentRunCompletedAt) ?? (statusMeta(r.runStatus).live ? new Date(now) : null);
  return end ? end.getTime() - start.getTime() : null;
}
function totalMs(r: AgentRun, now: number): number | null {
  const start = parseDate(r.pipelineSentToBusTime) ?? parseDate(r.busSentToAgentTime) ?? parseDate(r.agentRanTaskTime);
  if (!start) {
    return null;
  }
  const end = parseDate(r.agentRunCompletedAt) ?? (statusMeta(r.runStatus).live ? new Date(now) : null);
  return end ? end.getTime() - start.getTime() : null;
}
function segments(r: AgentRun, now: number): Segments {
  const p = parseDate(r.pipelineSentToBusTime);
  const b = parseDate(r.busSentToAgentTime);
  const a = parseDate(r.agentRanTaskTime);
  const c = parseDate(r.agentRunCompletedAt) ?? (statusMeta(r.runStatus).live ? new Date(now) : null);
  const bus = p && b ? b.getTime() - p.getTime() : 0;
  const pickup = b && a ? a.getTime() - b.getTime() : 0;
  const run = a && c ? c.getTime() - a.getTime() : 0;
  return { bus, pickup, run, total: bus + pickup + run };
}

@Component({
  selector: 'app-agent-runs',
  imports: [AgentRunsDateRangeComponent, AgentRunDrawerComponent],
  templateUrl: './agent-runs.component.html',
  styleUrl: './agent-runs.component.scss',
})
export class AgentRunsComponent {
  private _agentRunService = inject(AgentRunService);
  private _authService = inject(AuthService);
  private _telemetry = inject(TelemetryService);
  private _destroyRef = inject(DestroyRef);
  private _userDetails = toSignal(this._authService.userDetails$, { initialValue: null });

  private _agentDropdown = viewChild<ElementRef<HTMLElement>>('agentDropdown');

  private _runs = signal<AgentRun[]>([]);
  private _loading = signal<boolean>(false);
  private _error = signal<boolean>(false);

  private _statusFilter = signal<string>('all');
  private _agentFilter = signal<string>('all');
  private _search = signal<string>('');
  private _runForFrom = signal<string | null>(null);
  private _runForTo = signal<string | null>(null);
  private _completedFrom = signal<string | null>(null);
  private _completedTo = signal<string | null>(null);

  private _selectedId = signal<number | null>(null);

  private _collapsedGroups = signal<Set<string>>(new Set());
  private _rerunPending = signal<Set<number>>(new Set());
  private _rerunPendingContexts = signal<Set<string>>(new Set());
  private _rerunLocked = signal<Set<number>>(new Set());
  private _rerunRequestKeys = signal<Map<number, string>>(new Map());
  private _rerunFeedback = signal<RerunFeedback | null>(null);
  private _stopPending = signal<Set<number>>(new Set());
  private _stopRefreshHandle: ReturnType<typeof setTimeout> | null = null;
  private _canRequestReruns = computed(() => {
    const userDetails = this._userDetails();
    return environment.agentRunRerunsEnabled
      && userDetails !== null
      && (userDetails.hasAccess || userDetails.isAdmin);
  });
  private _canRequestStops = computed(() => {
    const userDetails = this._userDetails();
    return userDetails !== null && (userDetails.hasAccess || userDetails.isAdmin);
  });

  private _agentDropdownOpen = signal<boolean>(false);
  private _agentQuery = signal<string>('');

  readonly statusChips = [
    { val: 'all', label: 'All' },
    { val: 'active', label: 'In-flight' },
    { val: 'Succeeded', label: 'Succeeded' },
    { val: 'Failed', label: 'Failed' },
    { val: 'TimedOut', label: 'Timed Out' },
  ];

  loading = this._loading.asReadonly();
  error = this._error.asReadonly();
  statusFilter = this._statusFilter.asReadonly();
  agentFilter = this._agentFilter.asReadonly();
  search = this._search.asReadonly();
  selectedId = this._selectedId.asReadonly();
  rerunFeedback = this._rerunFeedback.asReadonly();
  agentDropdownOpen = this._agentDropdownOpen.asReadonly();
  agentQuery = this._agentQuery.asReadonly();
  totalRuns = computed(() => this._runs().length);

  agents = computed(() => [...new Set(this._runs().map(r => r.agentName))].sort());

  agentOptions = computed(() => {
    const q = this._agentQuery().toLowerCase();
    const options = [{ val: 'all', label: 'All agents' }, ...this.agents().map(a => ({ val: a, label: a }))];
    return options.filter(o => o.label.toLowerCase().includes(q));
  });

  agentTriggerLabel = computed(() => {
    const val = this._agentFilter();
    return val === 'all' ? 'All agents' : val;
  });

  kpis = computed<Kpis>(() => {
    const runs = this._runs();
    const total = runs.length;
    const succeeded = runs.filter(r => r.runStatus === 'Succeeded').length;
    const failed = runs.filter(r => r.runStatus === 'Failed' || r.runStatus === 'TimedOut').length;
    const inflight = runs.filter(r => ACTIVE_STATUSES.has(r.runStatus)).length;
    const reruns = runs.filter(r => r.attempt > 1).length;
    const now = Date.now();
    const durs = runs.map(r => runDurationMs(r, now)).filter((v): v is number => v != null);
    const avg = durs.length ? durs.reduce((a, b) => a + b, 0) / durs.length : null;
    const succRate = total ? Math.round((succeeded / total) * 100) : 0;
    return { total, succeeded, failed, inflight, reruns, avg: fmtDur(avg), succRate };
  });

  private _filteredRuns = computed<AgentRun[]>(() => {
    const status = this._statusFilter();
    const agent = this._agentFilter();
    const q = this._search().toLowerCase();
    const runForFrom = this._runForFrom();
    const runForTo = this._runForTo();
    const completedFrom = this._completedFrom();
    const completedTo = this._completedTo();

    const rows = this._runs().filter(r => {
      if (status === 'active' && !ACTIVE_STATUSES.has(r.runStatus)) {
        return false;
      }
      if (status !== 'all' && status !== 'active' && r.runStatus !== status) {
        return false;
      }
      if (agent !== 'all' && r.agentName !== agent) {
        return false;
      }
      if (runForFrom && (!r.runForDate || r.runForDate < runForFrom)) {
        return false;
      }
      if (runForTo && (!r.runForDate || r.runForDate > runForTo)) {
        return false;
      }
      if (completedFrom || completedTo) {
        const completedAt = parseDate(r.agentRunCompletedAt);
        const cd = completedAt ? toWatDateIso(completedAt) : null;
        if (!cd) {
          return false;
        }
        if (completedFrom && cd < completedFrom) {
          return false;
        }
        if (completedTo && cd > completedTo) {
          return false;
        }
      }
      if (q) {
        const hay = `${r.agentName} ${r.contextId} ${r.summary ?? ''} ${r.runStatus} ${r.agentRunId}`.toLowerCase();
        if (!hay.includes(q)) {
          return false;
        }
      }
      return true;
    });

    return [...rows].sort((x, y) => y.agentRunId - x.agentRunId);
  });

  rows = computed<RunRowVm[]>(() => {
    const now = Date.now();
    return this._filteredRuns().map(run => this._toRowVm(run, now));
  });

  groupedRunVms = computed<GroupVm[]>(() => {
    const now = Date.now();
    const collapsed = this._collapsedGroups();
    const filtered = this._filteredRuns();

    // Preserve first-seen order so the outer sort still drives group ordering.
    const groups = new Map<string, AgentRun[]>();
    filtered.forEach(run => {
      const existing = groups.get(run.contextId);
      if (existing) {
        existing.push(run);
      } else {
        groups.set(run.contextId, [run]);
      }
    });

    return Array.from(groups.entries()).map(([contextId, runs]) => {
      // Latest attempt first so a rerun surfaces above its initial run.
      const ordered = [...runs].sort((a, b) => b.attempt - a.attempt);
      const isCollapsed = collapsed.has(contextId);
      return {
        groupKey: contextId,
        groupLabel: ordered[0].agentName,
        attemptCount: ordered.length,
        hasReruns: ordered.length > 1,
        isCollapsed,
        rows: isCollapsed ? [] : ordered.map(run => this._toRowVm(run, now)),
      };
    });
  });

  filteredRunsCount = computed(() => this._filteredRuns().length);

  selectedVm = computed<DrawerVm | null>(() => {
    const id = this._selectedId();
    if (id == null) {
      return null;
    }
    const run = this._runs().find(r => r.agentRunId === id);
    if (!run) {
      return null;
    }
    const now = Date.now();
    const status = runStatusMeta(run);
    const seg = segments(run, now);
    const live = status.live && !run.agentRunCompletedAt;
    const isFail = run.runStatus === 'Failed' || run.runStatus === 'TimedOut';
    const total = totalMs(run, now);

    return {
      run,
      status,
      live,
      isFail,
      waterfall: seg.total <= 0 ? null : this._toWaterfall(seg),
      hops: this._buildHops(run, live),
      totalText: total != null ? `${fmtDur(total)}${live ? '…' : ''}` : null,
      createdText: fmtTime(run.createdDate),
      updatedText: fmtTime(run.updatedDate),
      groupKey: run.contextId,
      canRerun: run.supportsRerun
        && this._canRequestReruns()
        && !this._hasActiveAttemptInContext(run)
        && (!this._rerunPendingContexts().has(run.contextId) || this._rerunPending().has(run.agentRunId))
        && !this._rerunLocked().has(run.agentRunId),
      rerunPending: this._rerunPending().has(run.agentRunId),
      canStop: run.canStop && this._canRequestStops() && !this._stopPending().has(run.agentRunId),
      stopPending: this._stopPending().has(run.agentRunId),
    };
  });

  constructor() {
    this._destroyRef.onDestroy(() => this._clearStopRefresh());
    this._loadRuns();
  }

  loadRuns(): void {
    this._loadRuns();
  }

  setStatusFilter(val: string): void {
    this._statusFilter.set(val);
  }

  onSearch(value: string): void {
    this._search.set(value);
  }

  toggleAgentDropdown(): void {
    const open = !this._agentDropdownOpen();
    this._agentDropdownOpen.set(open);
    if (open) {
      this._agentQuery.set('');
    }
  }

  onAgentQuery(value: string): void {
    this._agentQuery.set(value);
  }

  selectAgent(val: string): void {
    this._agentFilter.set(val);
    this._agentDropdownOpen.set(false);
  }

  onRunForRange(range: DateRange): void {
    this._runForFrom.set(range.from);
    this._runForTo.set(range.to);
  }

  onCompletedRange(range: DateRange): void {
    this._completedFrom.set(range.from);
    this._completedTo.set(range.to);
  }

  toggleGroupCollapse(groupKey: string): void {
    const collapsed = new Set(this._collapsedGroups());
    if (collapsed.has(groupKey)) {
      collapsed.delete(groupKey);
    } else {
      collapsed.add(groupKey);
    }
    this._collapsedGroups.set(collapsed);
  }

  rerunGroup(group: GroupVm): void {
    const latest = group.rows[0]?.run;
    if (!latest) {
      return;
    }
    this._submitRerun(latest);
  }

  rerunSelected(): void {
    const vm = this.selectedVm();
    if (!vm || !vm.canRerun || vm.rerunPending) {
      return;
    }
    this._submitRerun(vm.run);
  }

  isRerunPending(agentRunId: number): boolean {
    return this._rerunPending().has(agentRunId);
  }

  stopSelected(): void {
    const vm = this.selectedVm();
    if (!vm || !vm.canStop || vm.stopPending) {
      return;
    }
    const agentRunId = vm.run.agentRunId;
    this._rerunFeedback.set(null);
    this._setStopPending(agentRunId, true);

    this._agentRunService
      .cancel(agentRunId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: result => {
          this._telemetry.trackAppEvent(TelemetryEvent.AgentRunStopRequested, { agentRunId, outcome: result.outcome });
          this._setStopPending(agentRunId, false);
          this._refreshRuns();
          // The user may have opened another run while the request was in flight.
          if (this._selectedId() !== agentRunId) {
            return;
          }
          switch (result.outcome) {
            case 'StopRequested':
              this._rerunFeedback.set({ text: 'Stop requested. The run will be cancelled within about a minute.', tone: 'success' });
              break;
            case 'Stopped':
              this._rerunFeedback.set({ text: 'The run was stopped.', tone: 'success' });
              break;
            case 'TooLate':
              this._rerunFeedback.set({ text: 'Too late to stop: results are already being saved, so the run will finish normally.', tone: 'info' });
              break;
            case 'AlreadyTerminal':
              this._rerunFeedback.set({ text: 'The run had already finished.', tone: 'info' });
              break;
            default:
              this._rerunFeedback.set({ text: 'The run could not be found.', tone: 'info' });
              break;
          }
        },
        error: error => {
          this._telemetry.trackCommandFailed('agent_run_stop', error, { agentRunId });
          this._setStopPending(agentRunId, false);
          if (this._selectedId() !== agentRunId) {
            return;
          }
          this._rerunFeedback.set({
            text: error instanceof HttpErrorResponse && error.status === 403
              ? 'You are not authorized to stop this agent run.'
              : 'We could not confirm the stop request. Retrying is safe.',
            tone: 'error',
          });
        },
      });
  }

  openDrawer(id: number): void {
    this._rerunFeedback.set(null);
    this._selectedId.set(id);
  }

  closeDrawer(): void {
    this._rerunFeedback.set(null);
    this._selectedId.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this._agentDropdownOpen()) {
      return;
    }
    const dropdown = this._agentDropdown()?.nativeElement;
    if (dropdown && !dropdown.contains(event.target as Node)) {
      this._agentDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._selectedId() != null) {
      this.closeDrawer();
    }
    if (this._agentDropdownOpen()) {
      this._agentDropdownOpen.set(false);
    }
  }

  private _loadRuns(onSettled?: () => void): void {
    this._loading.set(true);
    this._error.set(false);
    this._agentRunService
      .getAll()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: runs => {
          this._runs.set(runs);
          this._loading.set(false);
          this._scheduleStopRefresh(runs);
          onSettled?.();
        },
        error: () => {
          this._error.set(true);
          this._loading.set(false);
          onSettled?.();
        },
      });
  }

  // Refreshes without the loading state, so the table, filters, and focus stay in place.
  private _refreshRuns(): void {
    this._agentRunService
      .getAll()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: runs => {
          this._runs.set(runs);
          this._error.set(false);
          this._scheduleStopRefresh(runs);
        },
        // Keep polling on a transient failure, based on the last runs we have.
        error: () => this._scheduleStopRefresh(this._runs()),
      });
  }

  private _toMiniBar(seg: Segments): MiniBar {
    const pct = (v: number) => Math.max(v > 0 ? 3 : 0, (v / seg.total) * 100);
    return { bus: pct(seg.bus), pickup: pct(seg.pickup), run: pct(seg.run) };
  }

  private _submitRerun(run: AgentRun): void {
    const agentRunId = run.agentRunId;
    if (!run.supportsRerun
      || !this._canRequestReruns()
      || this._hasActiveAttemptInContext(run)
      || this._rerunPendingContexts().has(run.contextId)
      || this._rerunPending().has(agentRunId)
      || this._rerunLocked().has(agentRunId)) {
      return;
    }

    this._rerunFeedback.set(null);
    let requestKey = this._rerunRequestKeys().get(agentRunId);
    if (!requestKey) {
      requestKey = this._newRequestKey();
      const requestKeys = new Map(this._rerunRequestKeys());
      requestKeys.set(agentRunId, requestKey);
      this._rerunRequestKeys.set(requestKeys);
    }

    const pending = new Set(this._rerunPending());
    pending.add(agentRunId);
    this._rerunPending.set(pending);
    const pendingContexts = new Set(this._rerunPendingContexts());
    pendingContexts.add(run.contextId);
    this._rerunPendingContexts.set(pendingContexts);

    this._agentRunService
      .rerun({ agentRunId, requestKey })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: result => {
          this._telemetry.trackAppEvent(TelemetryEvent.AgentRunRerunRequested, { agentRunId, outcome: result.outcome });
          this._clearRerunPending(agentRunId);
          // Hold the context until history shows the new attempt, so siblings can't rerun against stale data.
          const releaseContext = () => this._releasePendingContext(run.contextId);
          if (result.outcome === 0) {
            this._lockRerun(agentRunId);
            this._clearRequestKey(agentRunId);
            const message = result.agentRun
              ? `Rerun attempt ${result.agentRun.attempt} was accepted and started.`
              : 'The rerun request was accepted and started.';
            this._rerunFeedback.set({ text: message, tone: 'success' });
            this._loadRuns(releaseContext);
            return;
          }

          if (result.outcome === 4 && result.agentRun) {
            this._rerunFeedback.set({
              text: `Rerun attempt ${result.agentRun.attempt} is reserved, but dispatch is unconfirmed. Delivery will retry automatically. Retry using the same request is safe.`,
              tone: 'info',
            });
            this._loadRuns(releaseContext);
            return;
          }

          releaseContext();
          this._clearRequestKey(agentRunId);
          switch (result.outcome) {
            case 1:
              this._rerunFeedback.set({ text: 'The source run could not be found. No rerun was created.', tone: 'info' });
              break;
            case 2:
              this._rerunFeedback.set({ text: 'A run for this execution is already in progress. No rerun was created.', tone: 'info' });
              break;
            case 3:
              this._rerunFeedback.set({ text: 'The rerun request conflicted with the current execution state. No rerun was created.', tone: 'info' });
              break;
            case 4:
              this._rerunFeedback.set({ text: 'Rerun publishing is disabled. No attempt was reserved.', tone: 'error' });
              break;
            default:
              this._rerunFeedback.set({ text: 'The rerun request was not accepted. No new attempt was created.', tone: 'error' });
              break;
          }
        },
        error: error => {
          this._telemetry.trackCommandFailed('agent_run_rerun', error, { agentRunId });
          this._clearRerunPending(agentRunId);
          this._releasePendingContext(run.contextId);
          if (error instanceof HttpErrorResponse && error.status === 403) {
            this._clearRequestKey(agentRunId);
            this._rerunFeedback.set({ text: 'You are not authorized to rerun this agent run.', tone: 'error' });
            return;
          }
          this._rerunFeedback.set({
            text: 'We could not confirm the rerun request. Retry it to safely reuse the same request and avoid a duplicate.',
            tone: 'error',
          });
        },
      });
  }

  private _hasActiveAttemptInContext(run: AgentRun): boolean {
    return this._runs().some(attempt =>
      attempt.contextId === run.contextId && ACTIVE_STATUSES.has(attempt.runStatus),
    );
  }

  private _setStopPending(agentRunId: number, pending: boolean): void {
    const next = new Set(this._stopPending());
    if (pending) {
      next.add(agentRunId);
    } else {
      next.delete(agentRunId);
    }
    this._stopPending.set(next);
  }

  // Keep refreshing while any stop is still in progress, so the page shows it finishing.
  private _scheduleStopRefresh(runs: AgentRun[]): void {
    this._clearStopRefresh();
    if (runs.some(isStopping)) {
      this._stopRefreshHandle = setTimeout(() => this._refreshRuns(), STOP_REFRESH_MS);
    }
  }

  private _clearStopRefresh(): void {
    if (this._stopRefreshHandle !== null) {
      clearTimeout(this._stopRefreshHandle);
      this._stopRefreshHandle = null;
    }
  }

  private _lockRerun(agentRunId: number): void {
    const locked = new Set(this._rerunLocked());
    locked.add(agentRunId);
    this._rerunLocked.set(locked);
  }

  private _clearRequestKey(agentRunId: number): void {
    const requestKeys = new Map(this._rerunRequestKeys());
    requestKeys.delete(agentRunId);
    this._rerunRequestKeys.set(requestKeys);
  }

  private _clearRerunPending(agentRunId: number): void {
    const pending = new Set(this._rerunPending());
    pending.delete(agentRunId);
    this._rerunPending.set(pending);
  }

  private _releasePendingContext(contextId: string): void {
    const pendingContexts = new Set(this._rerunPendingContexts());
    pendingContexts.delete(contextId);
    this._rerunPendingContexts.set(pendingContexts);
  }

  private _newRequestKey(): string {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
    return `rerun-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private _toRowVm(run: AgentRun, now: number): RunRowVm {
    const status = runStatusMeta(run);
    const seg = segments(run, now);
    const live = status.live && !run.agentRunCompletedAt;
    const isFail = run.runStatus === 'Failed' || run.runStatus === 'TimedOut';
    const duration = totalMs(run, now);
    return {
      run,
      status,
      isRerun: run.attempt > 1,
      runForDate: run.runForDate ?? '—',
      completedText: fmtTime(run.agentRunCompletedAt),
      completedInProgress: live,
      summary: run.summary,
      miniBar: seg.total <= 0 ? null : this._toMiniBar(seg),
      miniBarFailed: isFail,
      miniBarOpen: live,
      durationText: live ? `${fmtDur(duration)}…` : fmtDur(duration),
      durationOpen: live,
    };
  }

  private _toWaterfall(seg: Segments): WaterfallSeg {
    const pct = (v: number) => (v / seg.total) * 100;
    const label = (v: number, text: string) => (pct(v) > 14 ? text : '');
    return {
      bus: pct(seg.bus),
      pickup: pct(seg.pickup),
      run: pct(seg.run),
      busLabel: label(seg.bus, `bus wait ${fmtDur(seg.bus)}`),
      pickupLabel: label(seg.pickup, `pickup ${fmtDur(seg.pickup)}`),
      runLabel: label(seg.run, `run ${fmtDur(seg.run)}`),
    };
  }

  private _buildHops(run: AgentRun, live: boolean): HopVm[] {
    const p = parseDate(run.pipelineSentToBusTime);
    const b = parseDate(run.busSentToAgentTime);
    const a = parseDate(run.agentRanTaskTime);
    const c = parseDate(run.agentRunCompletedAt);
    const isFail = run.runStatus === 'Failed' || run.runStatus === 'TimedOut';
    const delta = (from: Date | null, to: Date | null): { text: string; muted: boolean } | null =>
      from && to ? { text: `+${fmtDur(to.getTime() - from.getTime())}`, muted: false } : null;

    return [
      {
        name: 'Pipeline sent to bus',
        col: 'PipelineSentToBusTime',
        time: fmtTime(run.pipelineSentToBusTime),
        delta: null,
        cls: run.pipelineSentToBusTime ? 'hop--done' : 'hop--pending',
      },
      {
        name: 'Bus sent to agent',
        col: 'BusSentToAgentTime',
        time: fmtTime(run.busSentToAgentTime),
        delta: delta(p, b),
        cls: run.busSentToAgentTime ? 'hop--done' : 'hop--pending',
      },
      {
        name: 'Agent ran task',
        col: 'AgentRanTaskTime',
        time: fmtTime(run.agentRanTaskTime),
        delta: delta(b, a),
        cls: run.agentRanTaskTime ? 'hop--done' : live ? 'hop--current' : 'hop--pending',
      },
      {
        name: 'Agent run completed',
        col: 'AgentRunCompletedAt',
        time: fmtTime(run.agentRunCompletedAt),
        delta: c ? { text: `+${fmtDur(c.getTime() - (a ? a.getTime() : c.getTime()))}`, muted: false } : live ? { text: 'running…', muted: true } : null,
        cls: c ? (isFail ? 'hop--failed' : 'hop--done') : live ? 'hop--current' : 'hop--pending',
      },
    ];
  }
}
